/**
 * Profile Results Banner Component - Colagea Hair Loss Context
 * 
 * Displays personalized quiz results at the top of product pages
 * when user arrives via quiz funnel with profile parameter
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, CheckCircle2, Clock, ChevronDown, Shield, AlertCircle, Droplets, Scissors, Zap } from 'lucide-react';

interface ProfileData {
  id: string;
  name: string;
  description: string;
  overallScore: number;
  dangerScore: number;
  insights: string[];
  stats: { label: string; value: string; danger?: boolean; score?: number }[];
}

interface BadgeConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  tags: string[];
  color: string;
}

const badgeConfigs: BadgeConfig[] = [
  { id: 'crown-thinning', label: 'Crown Thinning', icon: <TrendingUp className="w-3 h-3" />, tags: ['crown-thinning'], color: 'bg-red-100 text-red-700' },
  { id: 'receding-hairline', label: 'Receding Hairline', icon: <AlertCircle className="w-3 h-3" />, tags: ['receding-hairline', 'temples'], color: 'bg-orange-100 text-orange-700' },
  { id: 'diffuse-thinning', label: 'Diffuse Thinning', icon: <Droplets className="w-3 h-3" />, tags: ['diffuse-thinning'], color: 'bg-amber-100 text-amber-700' },
  { id: 'severe-loss', label: 'Severe Loss', icon: <AlertTriangle className="w-3 h-3" />, tags: ['severe-loss'], color: 'bg-red-100 text-red-700' },
  { id: 'excessive-shedding', label: 'Active Shedding', icon: <Droplets className="w-3 h-3" />, tags: ['excessive-shedding', 'rapid-loss'], color: 'bg-rose-100 text-rose-700' },
  { id: 'genetic', label: 'Genetic Pattern', icon: <Zap className="w-3 h-3" />, tags: ['genetic', 'hereditary'], color: 'bg-purple-100 text-purple-700' },
  { id: 'tried-treatments', label: 'Treatment Switcher', icon: <Shield className="w-3 h-3" />, tags: ['tried-minoxidil', 'tried-finasteride', 'tried-prp'], color: 'bg-blue-100 text-blue-700' },
  { id: 'stress-related', label: 'Stress Factor', icon: <Zap className="w-3 h-3" />, tags: ['stress-related', 'lifestyle'], color: 'bg-slate-100 text-slate-700' },
  { id: 'fine-hair', label: 'Fine Hair', icon: <Scissors className="w-3 h-3" />, tags: ['fine-hair', 'thin-texture'], color: 'bg-indigo-100 text-indigo-700' },
];

const profileConfigs: Record<string, ProfileData> = {
  'severe-hair-loss': {
    id: 'severe-hair-loss',
    name: 'Severe Hair Loss Profile',
    description: 'Your quiz results show advanced hair loss that requires immediate, intensive treatment.',
    overallScore: 22,
    dangerScore: 92,
    insights: [
      'Your follicles are actively miniaturizing and need urgent intervention',
      'Without treatment, dormant follicles risk permanent shutdown within 12-18 months',
      'The 4-Month Intensive Cure provides maximum follicle reactivation potential',
    ],
    stats: [
      { label: 'Follicle Health', value: 'Critical', danger: true, score: 18 },
      { label: 'Reactivation Window', value: 'Narrowing', danger: true, score: 25 },
      { label: 'Treatment Urgency', value: 'Immediate', danger: true, score: 15 },
    ],
  },
  'crown-thinning': {
    id: 'crown-thinning',
    name: 'Crown Thinning Profile',
    description: 'Crown thinning is the most treatable pattern. Your follicles are highly responsive to micro-infusion therapy.',
    overallScore: 42,
    dangerScore: 75,
    insights: [
      'Crown follicles respond exceptionally well to deep micro-infusion delivery',
      '90% of crown-thinning patients see visible improvement within 8 weeks',
      'The 2-Month Cure targets crown follicles effectively',
    ],
    stats: [
      { label: 'Crown Density', value: 'Reduced', danger: true, score: 40 },
      { label: 'Follicle Response', value: 'High Potential', danger: false, score: 75 },
      { label: 'Recovery Outlook', value: 'Favorable', danger: false, score: 65 },
    ],
  },
  'receding-hairline': {
    id: 'receding-hairline',
    name: 'Receding Hairline Profile',
    description: 'Hairline recession requires targeted delivery to frontal follicles. The micro-needle cap is designed for this.',
    overallScore: 45,
    dangerScore: 70,
    insights: [
      'Frontal follicles need targeted, deep delivery for reactivation',
      'Most users see visible baby hair growth within 5-6 weeks',
      'Focus application on the hairline zone for best results',
    ],
    stats: [
      { label: 'Hairline Recession', value: 'Active', danger: true, score: 35 },
      { label: 'Follicle Viability', value: 'Good', danger: false, score: 60 },
      { label: 'Treatment Match', value: 'Excellent', danger: false, score: 80 },
    ],
  },
  'diffuse-thinning': {
    id: 'diffuse-thinning',
    name: 'Diffuse Thinning Profile',
    description: 'Diffuse thinning affects the entire scalp. Comprehensive treatment across all zones is recommended.',
    overallScore: 38,
    dangerScore: 65,
    insights: [
      'Full-scalp thinning requires sustained, comprehensive treatment',
      'The 4-Month Cure provides the coverage needed for all affected zones',
      'Apply to all thinning areas for best results',
    ],
    stats: [
      { label: 'Overall Density', value: 'Below Normal', danger: true, score: 38 },
      { label: 'Scalp Coverage', value: 'Needs Full Treatment', danger: true, score: 45 },
      { label: 'Response Potential', value: 'Good', danger: false, score: 60 },
    ],
  },
  'early-prevention': {
    id: 'early-prevention',
    name: 'Early Stage Profile',
    description: "You're catching this early. Your follicles are still highly responsive to treatment.",
    overallScore: 65,
    dangerScore: 45,
    insights: [
      'Early intervention has the highest success rate for hair regrowth',
      'Your follicles are still active and responsive to treatment',
      'The 1-Month Cure should show visible thickening within 4-5 weeks',
    ],
    stats: [
      { label: 'Current Density', value: 'Slightly Reduced', danger: false, score: 65 },
      { label: 'Follicle Activity', value: 'Still Active', danger: false, score: 75 },
      { label: 'Prevention Window', value: 'Optimal', danger: false, score: 85 },
    ],
  },
  'treatment-switch': {
    id: 'treatment-switch',
    name: 'Treatment Switcher Profile',
    description: "Previous treatments didn't work because they couldn't reach deep enough. Colagea's micro-needle delivery changes that.",
    overallScore: 48,
    dangerScore: 60,
    insights: [
      'Topical treatments only reach the scalp surface, not the follicle root',
      'Colagea delivers active compounds 10x deeper than minoxidil',
      'No prescription needed, no systemic side effects',
    ],
    stats: [
      { label: 'Previous Treatment', value: 'Insufficient Depth', danger: true, score: 30 },
      { label: 'Follicle Accessibility', value: 'Improved with Micro-Needle', danger: false, score: 70 },
      { label: 'Expected Improvement', value: 'Significant', danger: false, score: 65 },
    ],
  },
  'hair-regrowth': {
    id: 'hair-regrowth',
    name: 'Hair Regrowth Candidate',
    description: "Based on your profile, Colagea's micro-infusion technology can help improve your hair density.",
    overallScore: 55,
    dangerScore: 50,
    insights: [
      'Your hair loss pattern is treatable with targeted micro-infusion',
      'Consistent daily use is key to seeing results',
      'Start with the 1-Month Cure to experience the difference',
    ],
    stats: [
      { label: 'Current State', value: 'Treatable', danger: false, score: 55 },
      { label: 'Treatment Match', value: 'Good Fit', danger: false, score: 70 },
      { label: 'Expected Timeline', value: '4-8 Weeks', danger: false, score: 60 },
    ],
  },
};

interface ProfileResultsBannerProps {
  profileId: string;
  email?: string;
  tags?: string[];
  labels?: string[];
  onRestartQuiz?: () => void;
  pageId?: string;
}

export function ProfileResultsBanner({ profileId, email, tags = [], labels = [], onRestartQuiz, pageId }: ProfileResultsBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const showBannerTimer = setTimeout(() => setIsVisible(true), 100);
    const expandTimer = setTimeout(() => setIsExpanded(true), 1800);
    return () => { clearTimeout(showBannerTimer); clearTimeout(expandTimer); };
  }, []);
  
  const profile = profileConfigs[profileId];
  if (!profile) return null;

  const matchedBadges = badgeConfigs.filter(badge => badge.tags.some(tag => tags.includes(tag)));
  const showBadges = matchedBadges.length > 0;

  const dangerLevel = profile.dangerScore >= 75 ? 'high' : profile.dangerScore >= 50 ? 'medium' : 'low';
  const dangerColor = dangerLevel === 'high' ? 'red' : dangerLevel === 'medium' ? 'amber' : 'yellow';
  
  const getScoreColor = (score: number) => {
    if (score < 40) return { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' };
    if (score < 60) return { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' };
    return { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' };
  };
  
  const scoreColors = getScoreColor(profile.overallScore);

  return (
    <div 
      data-profile-banner
      className={`border-b-2 overflow-hidden ${
        dangerLevel === 'high' ? 'bg-red-50/30 border-red-200/50' :
        dangerLevel === 'medium' ? 'bg-amber-50/30 border-amber-200/50' :
        'bg-yellow-50/30 border-yellow-200/50'
      }`}
      style={{
        transformOrigin: 'top',
        maxHeight: isVisible ? '1500px' : '0px',
        opacity: isVisible ? 1 : 0,
        transition: 'max-height 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease-out',
      }}
    >
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-start gap-4 flex-1">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
              dangerLevel === 'high' ? 'bg-red-100/50' :
              dangerLevel === 'medium' ? 'bg-amber-100/50' :
              'bg-yellow-100/50'
            }`}>
              {dangerLevel === 'high' ? (
                <AlertTriangle className={`w-7 h-7 text-${dangerColor}-600`} />
              ) : (
                <TrendingUp className={`w-7 h-7 text-${dangerColor}-600`} />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h3 className="text-xl font-bold text-[#1a1a1a] tracking-tight">
                  Your Hair Loss Analysis
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                  dangerLevel === 'high' ? 'bg-red-100/60 text-red-700' :
                  dangerLevel === 'medium' ? 'bg-amber-100/60 text-amber-700' :
                  'bg-yellow-100/60 text-yellow-700'
                }`}>
                  {dangerLevel === 'high' ? 'Urgent' : dangerLevel === 'medium' ? 'Important' : 'Attention Needed'}
                </span>
              </div>
              
              {showBadges && (
                <div className="hidden md:flex flex-wrap gap-2 mb-3">
                  {matchedBadges.map((badge) => (
                    <span key={badge.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
                      {badge.icon}
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}
              
              <p className="text-base text-[#555] leading-relaxed">{profile.description}</p>
              {email && <p className="text-sm text-[#888] mt-2">Full report sent to {email}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onRestartQuiz && (
              <button onClick={onRestartQuiz} className="p-2 hover:bg-white/50 rounded-lg transition-colors" title="Restart Analysis">
                <svg className="w-5 h-5 text-[#666]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
              </button>
            )}
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
              <ChevronDown className={`w-5 h-5 text-[#666] transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        <div 
          className={`grid md:grid-cols-2 gap-6 overflow-hidden transition-all duration-500 ease-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
          style={{ maxHeight: isExpanded ? '600px' : '0px', marginTop: isExpanded ? '0' : '-16px' }}
        >
          {/* LEFT: Stats */}
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5">
            <h4 className="text-base font-bold text-[#1a1a1a] mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Clock className="w-5 h-5" /> Your Hair Profile
            </h4>
            <div className={`${scoreColors.bg} rounded-lg p-4 mb-5`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#444]">Follicle Health Score</span>
                <span className={`text-3xl font-bold ${scoreColors.text}`}>
                  {profile.overallScore}<span className="text-lg">/100</span>
                </span>
              </div>
              <div className="relative h-2.5 bg-white/50 rounded-full overflow-hidden">
                <div className={`h-full ${scoreColors.bar} transition-all duration-1000 ease-out`} style={{ width: isExpanded ? `${profile.overallScore}%` : '0%' }} />
              </div>
            </div>
            <div className="space-y-4">
              {profile.stats.map((stat, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-[#444]">{stat.label}</span>
                    <span className={`text-sm font-bold ${stat.danger ? 'text-red-600' : 'text-[#1a1a1a]'}`}>{stat.value}</span>
                  </div>
                  {stat.score !== undefined && (
                    <div className="relative h-2 bg-white/50 rounded-full overflow-hidden">
                      <div className={`h-full transition-all duration-1000 ease-out ${stat.danger ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: isExpanded ? `${stat.score}%` : '0%', transitionDelay: `${index * 100}ms` }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Insights */}
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5">
            <h4 className="text-base font-bold text-[#1a1a1a] mb-4 flex items-center gap-2 uppercase tracking-wide">
              <CheckCircle2 className="w-5 h-5" /> Key Insights
            </h4>
            <ul className="space-y-3">
              {profile.insights.map((insight, index) => (
                <li key={index} className="text-sm text-[#555] flex items-start gap-3 leading-relaxed">
                  <span className="text-[#1a1a1a] font-bold text-base mt-0.5">•</span>
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-4 flex items-center justify-between gap-4">
          <p className="text-sm text-[#666] flex-1 min-w-0">
            Scroll down to see the treatment recommended for your profile
          </p>
          <button 
            onClick={() => {
              const heroSection = document.querySelector('[data-hero-section]');
              if (heroSection) {
                heroSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' });
              }
            }}
            className="px-4 py-2 text-sm font-semibold rounded-full border border-[#1a1a1a] text-[#1a1a1a] bg-transparent hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap flex-shrink-0"
          >
            Start My Cure
          </button>
        </div>
      </div>
    </div>
  );
}
